import React from "react"
import { ReportLayout } from "../../../components/reports"
import { Helmet } from "react-helmet"
import Header from "../../../components/Header"

const EventAdvocacyPlaybook = () => {
  const REPORT_ID = "event-advocacy-playbook"

  return (
    <ReportLayout
      title="The Event Advocacy Playbook - Turn Participants into Marketing Power"
      description="Learn how to transform your event participants into a powerful volunteer marketing army. Strategic playbook based on successful campaigns from major tech expos like GITEX Global."
      reportId={REPORT_ID}
      reportTitle=""
      reportSubtitle=""
      showHeader={false}
      keywords={[
        "event advocacy marketing, attendee marketing strategy, event participant engagement, volunteer marketing, event social sharing, attendee advocacy, user generated content events, event promotion tactics, attendee social media, event marketing playbook, participant engagement strategy, event word of mouth marketing, social proof events, attendee amplification, event referral marketing",
      ]}
      meta={[
        {
          name: "keywords",
          content:
            "event advocacy, participant marketing, attendee engagement, social sharing, event promotion, UGC strategy, word of mouth marketing",
        },
        { name: "robots", content: "index, follow" },
        { name: "author", content: "Premagic" },
        {
          name: "og:title",
          content:
            "The Event Advocacy Playbook | Turn Participants into Marketing Power",
        },
        {
          name: "og:description",
          content:
            "Transform your event participants into a powerful volunteer marketing army. Learn strategies from successful campaigns at major tech expos.",
        },
        { name: "og:type", content: "article" },
        {
          name: "og:url",
          content: "https://premagic.com/reports/event-advocacy-playbook/",
        },
        { name: "twitter:card", content: "summary_large_image" },
        {
          name: "twitter:title",
          content:
            "The Event Advocacy Playbook | Turn Participants into Marketing Power",
        },
        {
          name: "twitter:description",
          content:
            "Transform your event participants into a powerful volunteer marketing army with proven strategies.",
        },
      ]}
    >
      <Helmet>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link
          rel="preconnect"
          href="https://fonts.gstatic.com"
          crossOrigin="anonymous"
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap"
          rel="stylesheet"
        />
        <style>{`
          .playbook-body {
            font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          }
          .toc-scroll::-webkit-scrollbar {
            width: 4px;
          }
          .toc-scroll::-webkit-scrollbar-track {
            background: transparent;
          }
          .toc-scroll::-webkit-scrollbar-thumb {
            background-color: #e2e8f0;
            border-radius: 20px;
          }
          .text-gradient {
            background-clip: text;
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-image: linear-gradient(to right, #4f46e5, #ec4899);
          }
          html {
            scroll-behavior: smooth;
          }
          /* Override report container max-width for modern wide layout */
          .report-container {
            max-width: 1280px !important;
            width: 100% !important;
            margin: 0 auto !important;
            overflow: visible !important;
          }
          /* Mobile consistency improvements */
          @media (max-width: 768px) {
            .playbook-body header {
              padding-top: 2rem !important;
              padding-bottom: 2rem !important;
            }
            .playbook-body section {
              margin-bottom: 2.5rem !important;
            }
            .playbook-body h2 {
              font-size: 1.5rem !important;
              margin-bottom: 1rem !important;
            }
            .playbook-body h3 {
              font-size: 1.125rem !important;
              margin-bottom: 0.75rem !important;
              margin-top: 1.5rem !important;
            }
            .playbook-body h4 {
              font-size: 1rem !important;
              margin-bottom: 0.5rem !important;
            }
            .playbook-body p {
              font-size: 0.9375rem !important;
              line-height: 1.6 !important;
              margin-bottom: 1rem !important;
            }
            .bg-indigo-900 {
              padding: 1.25rem !important;
            }
            .playbook-body .space-y-8 > * + * {
              margin-top: 1.5rem !important;
            }
            .playbook-body .grid {
              gap: 1rem !important;
            }
          }
        `}</style>
      </Helmet>

      <div className="playbook-body bg-gray-50 text-gray-800 antialiased">
        {/* Site Header */}
        <div className="no-print">
          <Header />
        </div>

        {/* Hero Section */}
        <header className="pt-12 pb-12 bg-gradient-to-br from-gray-50 via-indigo-50 to-white border-b border-gray-100">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
            <div className="inline-flex items-center px-3 py-1 rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold uppercase tracking-wide mb-4">
              Strategy Guide
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-gray-900 tracking-tight mb-4 leading-tight">
              The Event Advocacy Playbook :<br />
              <span className="text-gradient">Leveraging Event Posters</span>
            </h1>
            <p className="text-xl sm:text-2xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
              How to Turn Your Participants into a Powerful Volunteer Marketing
              Army.
            </p>

            {/* Authors Section */}
            <div className="mt-6 flex flex-col items-center">
              <div className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-3">
                Authors
              </div>
              <div className="flex items-center gap-6 flex-wrap justify-center">
                {/* Author 1 */}
                <a
                  href="https://www.linkedin.com/in/mevinbabuc/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                >
                  <img
                    src="/mevin-chirayath.jpg"
                    alt="Mevin Chirayath"
                    className="w-10 h-10 rounded-full border-2 border-white shadow-sm"
                  />
                  <div className="text-left">
                    <div className="font-semibold text-gray-900 text-sm">
                      Mevin Chirayath
                    </div>
                    <div className="text-xs text-gray-600">
                      Co-founder & CTO
                    </div>
                  </div>
                </a>

                {/* Author 2 */}
                <a
                  href="https://www.linkedin.com/in/althafroshan/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                >
                  <img
                    src="/althaf-roshan.jpg"
                    alt="Althaf Roshan"
                    className="w-10 h-10 rounded-full border-2 border-white shadow-sm"
                  />
                  <div className="text-left">
                    <div className="font-semibold text-gray-900 text-sm">
                      Althaf Roshan
                    </div>
                    <div className="text-xs text-gray-600">Product</div>
                  </div>
                </a>
              </div>
              <div className="mt-3 text-xs text-gray-500">10 min read</div>
            </div>
          </div>
        </header>

        {/* Main Content Container */}
        <div className="w-full px-4 sm:px-6 lg:px-8 py-10">
          <div className="lg:grid lg:grid-cols-12 lg:gap-12 lg:items-start">
            {/* Sidebar / Table of Contents (Desktop) */}
            <aside className="hidden lg:block lg:col-span-3 sticky top-20 self-start">
              <nav className="toc-scroll pr-4 overflow-y-auto max-h-[calc(100vh-6rem)]">
                <h5 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">
                  Contents
                </h5>
                <ul className="space-y-2 text-sm">
                  <li>
                    <a
                      href="#core-strategy"
                      className="text-gray-900 font-medium hover:text-indigo-600 transition-colors"
                    >
                      The Core Strategy
                    </a>
                  </li>
                  <li>
                    <a
                      href="#mobile-reality"
                      className="text-gray-600 hover:text-indigo-600 transition-colors pl-3 border-l border-gray-200 block"
                    >
                      The Mobile Reality
                    </a>
                  </li>
                  <li>
                    <a
                      href="#psychology"
                      className="text-gray-600 hover:text-indigo-600 transition-colors pl-3 border-l border-gray-200 block"
                    >
                      The Psychology
                    </a>
                  </li>

                  <li>
                    <a
                      href="#phase-1"
                      className="text-gray-900 font-medium hover:text-indigo-600 transition-colors mt-3 block"
                    >
                      Phase 1: Strategy
                    </a>
                  </li>
                  <li>
                    <a
                      href="#goals"
                      className="text-gray-600 hover:text-indigo-600 transition-colors pl-3 border-l border-gray-200 block"
                    >
                      Step 1: Goals
                    </a>
                  </li>
                  <li>
                    <a
                      href="#segmentation"
                      className="text-gray-600 hover:text-indigo-600 transition-colors pl-3 border-l border-gray-200 block"
                    >
                      Step 2: Segmentation
                    </a>
                  </li>
                  <li>
                    <a
                      href="#personalization"
                      className="text-gray-600 hover:text-indigo-600 transition-colors pl-3 border-l border-gray-200 block"
                    >
                      Step 3: Personalization
                    </a>
                  </li>

                  <li>
                    <a
                      href="#phase-2"
                      className="text-gray-900 font-medium hover:text-indigo-600 transition-colors mt-3 block"
                    >
                      Phase 2: Design
                    </a>
                  </li>
                  <li>
                    <a
                      href="#format"
                      className="text-gray-600 hover:text-indigo-600 transition-colors pl-3 border-l border-gray-200 block"
                    >
                      1. The Format
                    </a>
                  </li>
                  <li>
                    <a
                      href="#design-principles"
                      className="text-gray-600 hover:text-indigo-600 transition-colors pl-3 border-l border-gray-200 block"
                    >
                      2. Principles
                    </a>
                  </li>
                  <li>
                    <a
                      href="#checklist"
                      className="text-gray-600 hover:text-indigo-600 transition-colors pl-3 border-l border-gray-200 block"
                    >
                      3. Checklist
                    </a>
                  </li>

                  <li>
                    <a
                      href="#phase-3"
                      className="text-gray-900 font-medium hover:text-indigo-600 transition-colors mt-3 block"
                    >
                      Phase 3: Execution
                    </a>
                  </li>
                  <li>
                    <a
                      href="#touchpoints"
                      className="text-gray-600 hover:text-indigo-600 transition-colors pl-3 border-l border-gray-200 block"
                    >
                      Step 4: Touchpoints
                    </a>
                  </li>
                  <li>
                    <a
                      href="#email-campaigns"
                      className="text-gray-600 hover:text-indigo-600 transition-colors pl-3 border-l border-gray-200 block"
                    >
                      Step 5: Email
                    </a>
                  </li>
                  <li>
                    <a
                      href="#messaging"
                      className="text-gray-600 hover:text-indigo-600 transition-colors pl-3 border-l border-gray-200 block"
                    >
                      Step 6: Messaging
                    </a>
                  </li>

                  <li>
                    <a
                      href="#phase-4"
                      className="text-gray-900 font-medium hover:text-indigo-600 transition-colors mt-3 block"
                    >
                      Phase 4: Optimization
                    </a>
                  </li>
                  <li>
                    <a
                      href="#metrics"
                      className="text-gray-600 hover:text-indigo-600 transition-colors pl-3 border-l border-gray-200 block"
                    >
                      Step 7: Metrics
                    </a>
                  </li>
                  <li>
                    <a
                      href="#iterate"
                      className="text-gray-600 hover:text-indigo-600 transition-colors pl-3 border-l border-gray-200 block"
                    >
                      Step 8: Iterate
                    </a>
                  </li>
                  <li>
                    <a
                      href="#automate"
                      className="text-gray-600 hover:text-indigo-600 transition-colors pl-3 border-l border-gray-200 block"
                    >
                      Step 9: Automate
                    </a>
                  </li>
                </ul>
              </nav>
            </aside>

            {/* Main Text Content */}
            <main className="lg:col-span-9 w-full">
              {/* Core Strategy Section */}
              <section id="core-strategy" className="mb-10">
                <div className="bg-indigo-900 rounded-2xl p-6 md:p-8 text-white shadow-xl relative overflow-hidden">
                  {/* Decorative BG */}
                  <div className="absolute top-0 right-0 -mt-10 -mr-10 w-40 h-40 bg-pink-500 opacity-20 rounded-full blur-3xl"></div>
                  <div className="absolute bottom-0 left-0 -mb-10 -ml-10 w-40 h-40 bg-cyan-500 opacity-20 rounded-full blur-3xl"></div>

                  <h2 className="text-2xl md:text-3xl font-bold mb-3 relative z-10 text-white">
                    The Core Strategy
                  </h2>

                  <div className="relative z-10">
                    <h3 className="text-pink-300 font-bold uppercase text-xs tracking-wider mb-2">
                      The Problem Statement
                    </h3>
                    <p className="text-lg font-medium leading-relaxed text-white">
                      Marketing focuses on{" "}
                      <span className="text-white border-b-2 border-pink-300">
                        "Register Now"
                      </span>
                      , but humans focus on{" "}
                      <span className="text-white border-b-2 border-cyan-300">
                        "Look at Me"
                      </span>
                      .
                    </p>
                    <p className="mt-2 text-indigo-100 text-base">
                      Most advocacy campaigns fail because they prioritize the
                      event organizer's needs over the attendee's social needs.
                      To fix this, we must shift the creative focus from
                      promotion to <em>personal storytelling</em>.
                    </p>
                  </div>
                </div>
              </section>

              {/* Mobile Reality */}
              <section id="mobile-reality" className="mb-10">
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                  The Mobile Reality: The "Browse-to-Buy" Gap
                </h2>
                <p className="text-base text-gray-700 mb-6 leading-relaxed">
                  Most people scroll on their phone and buy tickets on their
                  laptop. That specific behavior shapes everything in this
                  guide.
                </p>

                {/* Visual Chart */}
                <div className="space-y-4 mb-6 bg-white p-5 rounded-xl border border-gray-200 shadow-sm max-w-md">
                  <div>
                    <div className="flex justify-between text-sm font-semibold mb-1">
                      <span className="text-gray-700">Social Browsing</span>
                      <span className="text-gray-700">68% Mobile</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-3">
                      <div
                        className="bg-indigo-500 h-3 rounded-full"
                        style={{ width: "68%" }}
                      ></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm font-semibold mb-1">
                      <span className="text-gray-700">Ticket Purchases</span>
                      <span className="text-gray-700">60% Desktop</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-3">
                      <div
                        className="bg-gray-400 h-3 rounded-full"
                        style={{ width: "40%" }}
                      ></div>
                    </div>
                  </div>
                </div>

                <div className="bg-indigo-50 border-l-4 border-indigo-500 p-4 rounded-r-lg">
                  <p className="text-base text-indigo-900 font-medium">
                    <strong>The Implication:</strong> Your advocacy posters must
                    be designed for <strong>mobile-first consumption</strong>.
                    The creative has one job: to hook the user on a small screen
                    effectively enough that they return to register later on a
                    big screen. A strong design bridges this gap by keeping the
                    event top-of-mind.
                  </p>
                </div>
              </section>

              {/* Psychology */}
              <section id="psychology" className="mb-12">
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                  The Psychology of Advocacy
                </h2>
                <p className="text-base text-gray-700 mb-6 leading-relaxed">
                  To build a viral engine, you must align with human behavior.
                </p>

                <div className="grid md:grid-cols-2 gap-5">
                  {/* Card 1 */}
                  <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                    <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center mb-3 text-indigo-600">
                      <svg
                        className="w-6 h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C13 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z"
                        />
                      </svg>
                    </div>
                    <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">
                      The Fuel (Why Attendees Share)
                    </h3>
                    <p className="text-base text-gray-600 mb-3">
                      They don't share for you; they share for themselves.
                    </p>
                    <ul className="space-y-1.5 text-base text-gray-700">
                      <li className="flex items-start">
                        <span className="text-indigo-500 mr-2">✓</span>{" "}
                        <strong>Belonging:&nbsp;</strong> "I'm part of this
                        community"
                      </li>
                      <li className="flex items-start">
                        <span className="text-indigo-500 mr-2">✓</span>{" "}
                        <strong>Credibility:&nbsp;</strong> "I'm a professional"
                      </li>
                      <li className="flex items-start">
                        <span className="text-indigo-500 mr-2">✓</span>{" "}
                        <strong>Excitement:&nbsp;</strong> Anticipation
                      </li>
                    </ul>
                  </div>
                  {/* Card 2 */}
                  <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                    <div className="w-10 h-10 bg-pink-100 rounded-lg flex items-center justify-center mb-3 text-pink-600">
                      <svg
                        className="w-6 h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M13 10V3L4 14h7v7l9-11h-7z"
                        />
                      </svg>
                    </div>
                    <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">
                      The Spark (Why Networks Click)
                    </h3>
                    <p className="text-base text-gray-600 mb-3">
                      Drivers for traffic from social feeds.
                    </p>
                    <ul className="space-y-1.5 text-base text-gray-700">
                      <li className="flex items-start">
                        <span className="text-pink-500 mr-2">✓</span>{" "}
                        <strong>Curiosity:&nbsp;</strong> To explore what it is
                      </li>
                      <li className="flex items-start">
                        <span className="text-pink-500 mr-2">✓</span>{" "}
                        <strong>Trust:&nbsp;</strong> Friend &gt; Ad
                      </li>
                      <li className="flex items-start">
                        <span className="text-pink-500 mr-2">✓</span>{" "}
                        <strong>FOMO:&nbsp;</strong> Fear of Missing Out
                      </li>
                    </ul>
                  </div>
                </div>
              </section>

              <hr className="border-gray-200 mb-10" />

              {/* Phase 1 */}
              <section id="phase-1" className="mb-10">
                <span className="text-indigo-600 font-bold tracking-widest uppercase text-xs mb-2 block">
                  Phase 1
                </span>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                  Strategy & Preparation
                </h2>
                <p className="text-base text-gray-700 mb-6 leading-relaxed">
                  A successful campaign starts long before the first email is
                  sent.
                </p>

                <div className="space-y-8">
                  <div id="goals">
                    <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">
                      <span role="img" aria-label="checkmark">
                        ✅
                      </span>{" "}
                      Step 1: Define Clear Goals
                    </h3>
                    <p className="text-base text-gray-700 mb-2">
                      Be specific. Don't just aim for "engagement".
                    </p>
                    <div className="bg-gray-50 p-3 rounded-lg border-l-4 border-green-500 text-sm text-gray-700 font-mono">
                      "Generate 45,000 referral clicks to the registration page"
                      <br />
                      "Activate 10,000 advocates sharing on LinkedIn"
                    </div>
                  </div>

                  <div id="segmentation">
                    <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">
                      <span role="img" aria-label="checkmark">
                        ✅
                      </span>{" "}
                      Step 2: Segment Your Audience
                    </h3>
                    <p className="text-base text-gray-700">
                      Do not use a single template. Create tailored designs for
                      each key group:
                    </p>
                    <div className="flex flex-wrap gap-2 mt-2">
                      <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium">
                        Attendees
                      </span>
                      <span className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm font-medium">
                        Speakers
                      </span>
                      <span className="px-3 py-1 bg-pink-100 text-pink-700 rounded-full text-sm font-medium">
                        Exhibitors
                      </span>
                      <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium">
                        Sponsors & Partners
                      </span>
                      <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium">
                        Investors / VIPs
                      </span>
                    </div>
                  </div>

                  <div id="personalization">
                    <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">
                      <span role="img" aria-label="checkmark">
                        ✅
                      </span>{" "}
                      Step 3: Master Personalization
                    </h3>
                    <p className="text-base text-gray-700 mb-3">
                      True personalization drives sharing. Go beyond just{" "}
                      <code className="bg-gray-200 px-2 py-0.5 rounded text-xs">
                        [First Name]
                      </code>
                      .
                    </p>
                    <ul className="list-disc list-inside space-y-1 text-base text-gray-700 mb-3 ml-2">
                      <li>
                        <strong>Essentials:</strong> Attendee Name, Company
                        Name, Profile Picture.
                      </li>
                      <li>
                        <strong>Advanced:</strong> Use smart enrichment to pull
                        in company logos based on email domains for exhibitors.
                      </li>
                    </ul>
                    <div className="bg-indigo-50 rounded-xl p-4 border border-indigo-100">
                      <h4 className="font-bold text-indigo-900 text-base font-semibold uppercase mb-1">
                        Best Practice: The Introvert Fix
                      </h4>
                      <p className="text-base text-indigo-800">
                        Offer an alternative poster version that uses a{" "}
                        <strong>
                          company logo instead of a profile picture
                        </strong>
                        . This caters to camera-shy participants and
                        significantly increases share rates.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Phase 2 */}
              <section id="phase-2" className="mb-10">
                <span className="text-indigo-600 font-bold tracking-widest uppercase text-xs mb-2 block">
                  Phase 2
                </span>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                  Design for Impact
                </h2>
                <p className="text-base text-gray-700 mb-6 leading-relaxed">
                  <strong>The Stakes:</strong> A well-executed design is a
                  performance lever. A good design can lift conversions by 5%,
                  while a poor one breaks the conversion loop.
                </p>

                <div id="format" className="mb-8">
                  <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-4">
                    1. The Format (Mobile-Optimized)
                  </h3>
                  <div className="grid sm:grid-cols-3 gap-6">
                    <div className="sm:col-span-1">
                      {/* Visual representation of 4:5 */}
                      <div className="bg-gray-200 rounded-lg w-full aspect-[4/5] flex items-center justify-center relative shadow-inner">
                        <div className="text-center">
                          <span className="block text-xl font-bold text-gray-700">
                            4:5
                          </span>
                          <span className="text-xs text-gray-600">
                            1080 x 1350 px
                          </span>
                        </div>
                        {/* Phone overlay hint */}
                        <div className="absolute bottom-4 left-0 right-0 text-center">
                          <span className="inline-block px-2 py-0.5 bg-white/50 rounded text-xs text-gray-600">
                            Mobile Optimized
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="sm:col-span-2 flex flex-col justify-center">
                      <p className="text-base text-gray-700">
                        Strict adherence to these dimensions is required to
                        maximize visibility in social feeds.{" "}
                        <strong>Why:</strong> Square or tall formats deliver
                        higher visibility than landscape. This size occupies the
                        maximum screen real estate on a phone, ensuring your
                        message isn't scrolled past.
                      </p>
                    </div>
                  </div>
                </div>

                <div id="design-principles" className="space-y-6 mb-8">
                  <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-4">
                    2. Core Design Principles
                  </h3>

                  {/* Design Principles Visual */}
                  <div className="my-6">
                    <img
                      src="/event-advocacy-design-principles.png"
                      alt="Core Design Principles Visual Guide"
                      className="w-full rounded-lg shadow-md"
                    />
                  </div>

                  {/* Principle A */}
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold">
                      A
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 text-base md:text-lg mb-1">
                        "Personal First" Hierarchy
                      </h4>
                      <p className="text-base text-gray-700 mb-1">
                        People share to boost their own credibility. Therefore,
                        the attendee is the hero, not the event logo.
                      </p>
                      <p className="text-base text-gray-700">
                        <strong>The Rule:</strong> The attendee's Name, Company,
                        and Face must be unmissable.
                      </p>
                      <p className="text-base text-gray-700">
                        <strong>Typography:</strong> Name &gt; Professional
                        Title.
                      </p>
                      <p className="text-base text-indigo-600 font-semibold mt-1">
                        Effect: Validates ego, 3x more shares.
                      </p>
                    </div>
                  </div>

                  {/* Principle B */}
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold">
                      B
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 text-base md:text-lg mb-1">
                        Communicate Scale & Importance (FOMO)
                      </h4>
                      <p className="text-base text-gray-700 mb-1">
                        The design must instantly communicate that this event is
                        "the place to be".
                      </p>
                      <p className="text-base text-gray-700">
                        <strong>The Rule:</strong> Use impactful images and
                        taglines (e.g., "The World's Largest Tech Show").
                      </p>
                      <p className="text-base text-indigo-600 font-semibold mt-1">
                        Effect: Establishes credibility & creates FOMO.
                      </p>
                    </div>
                  </div>

                  {/* Principle C */}
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold">
                      C
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 text-base md:text-lg mb-1">
                        Ruthless Clarity & Minimal Clutter
                      </h4>
                      <p className="text-base text-gray-700 mb-1">
                        Mobile screens have limited space. Any extra visual
                        noise hurts performance.
                      </p>
                      <p className="text-base text-gray-700">
                        <strong>The Rule:</strong> The design must feel open and
                        easy to digest. Use strong visual hierarchy.
                      </p>
                    </div>
                  </div>

                  {/* Principle D */}
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold">
                      D
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 text-base md:text-lg mb-1">
                        Visual Storytelling (The Vibe)
                      </h4>
                      <p className="text-base text-gray-700 mb-1">
                        Don't just use a plain background.
                      </p>
                      <p className="text-base text-gray-700">
                        <strong>The Rule:</strong> Use backgrounds that capture
                        the event's energy, personality, or iconic location
                        (e.g., city skyline).
                      </p>
                      <p className="text-base text-indigo-600 font-semibold mt-1">
                        Effect: Stops the scroll. Feels like an "experience"
                        rather than a receipt.
                      </p>
                    </div>
                  </div>

                  {/* Example Images */}
                  <div className="mt-8">
                    <h4 className="font-bold text-gray-900 text-base md:text-lg mb-4">
                      Example Designs
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="rounded-lg overflow-hidden shadow-md">
                        <img
                          src="/example-design-1.webp"
                          alt="Event advocacy design example 1"
                          className="w-full h-auto object-cover"
                        />
                      </div>
                      <div className="rounded-lg overflow-hidden shadow-md">
                        <img
                          src="/example-design-2.webp"
                          alt="Event advocacy design example 2"
                          className="w-full h-auto object-cover"
                        />
                      </div>
                      <div className="rounded-lg overflow-hidden shadow-md">
                        <img
                          src="/example-design-3.webp"
                          alt="Event advocacy design example 3"
                          className="w-full h-auto object-cover"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div id="checklist">
                  <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-4">
                    3. Essential Elements Checklist
                  </h3>
                  <div className="bg-gray-50 p-4 rounded-xl">
                    <ul className="list-disc list-inside space-y-2 text-base text-gray-700">
                      <li>
                        <strong>Attendee:</strong> High-quality Profile Image,
                        Name (Big/Bold), Professional Title.
                      </li>
                      <li>
                        <strong>Event:</strong> Dates, Event Logo.
                      </li>
                      <li>
                        <strong>Location:</strong> Venue, City, or Country
                        (Highlight the most globally recognizable).
                      </li>
                      <li>
                        <strong>Color:</strong> Prioritize Event Colors over
                        corporate brand colors for an organic look.
                      </li>
                    </ul>
                  </div>
                </div>
              </section>

              <hr className="border-gray-200 mb-10" />

              {/* Phase 3 */}
              <section id="phase-3" className="mb-10">
                <span className="text-indigo-600 font-bold tracking-widest uppercase text-xs mb-2 block">
                  Phase 3
                </span>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                  Execution & Distribution
                </h2>
                <p className="text-base text-gray-700 mb-6 leading-relaxed">
                  Timing and channel selection are critical to maximizing
                  engagement.
                </p>

                <div className="space-y-8">
                  <div id="touchpoints">
                    <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">
                      <span role="img" aria-label="checkmark">
                        ✅
                      </span>{" "}
                      Step 4: Integrate into Key Touchpoints
                    </h3>
                    <p className="text-base text-gray-700 mb-2">
                      Reach your audience when their excitement is at its peak.
                    </p>
                    <ul className="list-disc list-inside space-y-1.5 text-base text-gray-700 ml-2">
                      <li>
                        <strong>"Quick Register" Button (Frictionless):</strong>{" "}
                        Social login = Instant poster.
                      </li>
                      <li>
                        <strong>Confirmation Page Widget:</strong> Embed on
                        "Thank You" page. Target: 30%+ engagement.
                      </li>
                      <li>
                        <strong>Management Portals:</strong> Embed native
                        widgets in Speaker/Exhibitor dashboards.
                      </li>
                    </ul>
                  </div>

                  <div id="email-campaigns">
                    <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">
                      <span role="img" aria-label="checkmark">
                        ✅
                      </span>{" "}
                      Step 5: Launch Hyper-Personalized Email Campaigns
                    </h3>
                    <p className="text-base text-gray-700 mb-2">
                      This is often the highest-performing channel.
                    </p>
                    <ul className="list-disc list-inside space-y-1.5 text-base text-gray-700 ml-2">
                      <li>
                        <strong>Timing:</strong> Tuesdays are best. Impact lasts
                        2-3 days.
                      </li>
                      <li>
                        <strong>Show, Don't Tell:</strong> Embed the{" "}
                        <em>live preview</em> of their poster in the email body.
                      </li>
                      <li>
                        <strong>Urgency:</strong> Frame subject lines as tasks
                        ("Action Required: Your Event Post is Ready").
                      </li>
                      <li>
                        <strong>Trust:</strong> Include social proof ("Join
                        5,000+ others who have shared!").
                      </li>
                    </ul>
                  </div>

                  <div id="messaging">
                    <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">
                      <span role="img" aria-label="checkmark">
                        ✅
                      </span>{" "}
                      Step 6: Leverage Targeted Messaging
                    </h3>
                    <ul className="list-disc list-inside space-y-1.5 text-base text-gray-700 ml-2">
                      <li>
                        <strong>WhatsApp:</strong> Use this for high-value
                        segments like Speakers and Exhibitors, especially over
                        weekends when email inboxes are ignored.
                      </li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* Phase 4 */}
              <section id="phase-4" className="mb-12">
                <span className="text-indigo-600 font-bold tracking-widest uppercase text-xs mb-2 block">
                  Phase 4
                </span>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                  Optimization & Analysis
                </h2>
                <p className="text-base text-gray-700 mb-6 leading-relaxed">
                  Launch is just the beginning. Real success comes from
                  iteration.
                </p>

                <div className="space-y-8">
                  <div id="metrics">
                    <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">
                      <span role="img" aria-label="checkmark">
                        ✅
                      </span>{" "}
                      Step 7: Monitor Actionable Metrics
                    </h3>
                    <p className="text-base text-gray-700 mb-2">
                      Track the full funnel to find bottlenecks:
                    </p>
                    <ul className="list-disc list-inside space-y-1.5 text-base text-gray-700 ml-2">
                      <li>
                        <strong>Email Clicks:</strong> Are people clicking
                        through to see their poster?
                      </li>
                      <li>
                        <strong>On-Page Engagement:</strong> How many interact
                        with the widget?
                      </li>
                      <li>
                        <strong>Generation vs. Share Rate:</strong> Identify the
                        drop-off between creating and posting.
                      </li>
                      <li>
                        <strong>Referral Clicks:</strong> Identify your most
                        effective advocate segments.
                      </li>
                    </ul>
                  </div>

                  <div id="iterate">
                    <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">
                      <span role="img" aria-label="checkmark">
                        ✅
                      </span>{" "}
                      Step 8: Iterate Based on Data
                    </h3>
                    <ul className="list-disc list-inside space-y-1.5 text-base text-gray-700 ml-2">
                      <li>
                        <strong>Heatmaps:</strong> Use Microsoft Clarity to see
                        user engagement. Stuck? Move the widget.
                      </li>
                      <li>
                        <strong>Social Trust:</strong> Add "12,500+ Posters
                        Created!" counters to the widget.
                      </li>
                      <li>
                        <strong>A/B Testing:</strong> Test different taglines.
                      </li>
                    </ul>
                  </div>

                  <div id="automate">
                    <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">
                      <span role="img" aria-label="checkmark">
                        ✅
                      </span>{" "}
                      Step 9: Automate the Follow-Up
                    </h3>
                    <p className="text-base text-gray-700 mb-2">
                      Many people generate a poster but forget to share it.
                    </p>
                    <div className="bg-indigo-50 p-3 rounded-lg border border-indigo-100">
                      <p className="text-base text-indigo-900 font-semibold">
                        <strong>The Nudge:</strong> Set up an automated email
                        3-5 days after generation if no share is detected.
                      </p>
                      <p className="text-sm text-indigo-800 mt-1">
                        "Ready to share your poster? Let your network know
                        you're attending!"
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Closing */}
              <section className="bg-indigo-600 rounded-2xl p-8 text-center text-white relative overflow-hidden">
                <div className="relative z-10">
                  <h2 className="text-2xl md:text-3xl font-bold mb-3 text-white">
                    The Strategic Mindset
                  </h2>
                  <p className="text-base text-white max-w-2xl mx-auto mb-6 leading-relaxed">
                    An advocacy poster campaign is not just another box to
                    check. The difference between a forgotten campaign and a
                    viral engine is treating it as a{" "}
                    <strong>core marketing strategy</strong>. When you give this
                    channel the thought and execution it deserves, you generate
                    authentic, organic social proof that paid ads can't buy.
                  </p>
                  <a
                    href="/create-event-marketing-posters/"
                    className="inline-block bg-white text-indigo-600 font-bold py-2.5 px-6 rounded-full shadow-lg hover:bg-gray-50 transition-colors"
                  >
                    Automate Your Event Posters
                  </a>
                </div>
                {/* BG Pattern */}
                <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-indigo-500 to-indigo-700 opacity-50 z-0"></div>
              </section>
            </main>
          </div>
        </div>
      </div>
    </ReportLayout>
  )
}

export default EventAdvocacyPlaybook
